import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { logger } from '@/lib/logger';
import {
  OFF_MAINTENANCE_STATE,
  subscribeToMaintenance,
  type MaintenanceState,
} from '@/services/maintenanceService';

export type MaintenanceAvailability = 'ready' | 'unknown';

interface MaintenanceContextValue {
  state: MaintenanceState;
  loading: boolean;
  error: Error | null;
  availability: MaintenanceAvailability;
}

const MaintenanceContext = createContext<MaintenanceContextValue | undefined>(undefined);

export function useMaintenance(): MaintenanceContextValue {
  const context = useContext(MaintenanceContext);
  if (!context) throw new Error('useMaintenance deve essere usato all\'interno di MaintenanceProvider');
  return context;
}

export function MaintenanceProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<MaintenanceState>(OFF_MAINTENANCE_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [availability, setAvailability] = useState<MaintenanceAvailability>('unknown');

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      setAvailability('unknown');
      return undefined;
    }

    if (!user) {
      setState(OFF_MAINTENANCE_STATE);
      setError(null);
      setAvailability('ready');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    setAvailability('unknown');
    let active = true;
    const unsubscribe = subscribeToMaintenance(
      (nextState) => {
        if (!active) return;
        setState(nextState);
        setError(null);
        setAvailability('ready');
        setLoading(false);
      },
      (nextError) => {
        if (!active) return;
        const normalizedError = nextError instanceof Error ? nextError : new Error(String(nextError));
        logger.error('Errore lettura stato manutenzione, continuo in fail-open:', normalizedError);
        setState(OFF_MAINTENANCE_STATE);
        setError(normalizedError);
        setAvailability('unknown');
        setLoading(false);
      },
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [authLoading, user?.uid]);

  return (
    <MaintenanceContext.Provider value={{ state, loading, error, availability }}>
      {children}
    </MaintenanceContext.Provider>
  );
}
